import moment from 'moment'
import { useLayoutEffect, useMemo, useRef } from 'react'

import { BookablesByDate, sortedBookablesByDateForRange } from '../../models/Bookable'
import { CalendarFilter } from '../../models/CalendarFilter'
import { User } from '../../models/User'
import { isToday } from '../../utils/dates'
import Card from '../app/design/Card'
import { CalendarBookableTable } from './CalendarBookableTable'
import { CalendarEmptyState } from './CalendarEmptyState'

type Props = {
    heightOffset: number
    currentDate: moment.Moment
    pros: User[]
    filter: CalendarFilter
    bookablesByDate: BookablesByDate
}

export const CalendarAgenda = ({
    heightOffset,
    currentDate,
    pros,
    filter,
    bookablesByDate,
}: Props) => {
    const tableRef = useRef<{
        headerRef: React.RefObject<HTMLTableSectionElement>
        todayRowRef: React.RefObject<HTMLTableRowElement>
    }>(null)

    useLayoutEffect(() => {
        if (!tableRef.current) return

        const { headerRef, todayRowRef } = tableRef.current
        const elem = !isToday(currentDate) ? headerRef.current : todayRowRef.current

        if (elem) {
            elem.scrollIntoView({
                behavior: 'smooth',
                block: !isToday(currentDate) ? 'end' : 'start',
            })
        }
    }, [currentDate])

    const bookablesByDateForWeek = useMemo(
        () =>
            sortedBookablesByDateForRange(
                bookablesByDate,
                currentDate.clone().startOf('week'),
                currentDate.clone().endOf('week')
            ),
        [bookablesByDate, currentDate]
    )

    return (
        <Card
            className="mt-6 mb-8 overflow-x-clip overflow-y-auto overscroll-contain"
            style={{
                maxHeight: `calc(100% - ${heightOffset}px)`,
                maxWidth: '100%',
                minWidth: '1024px',
            }}
        >
            <CalendarBookableTable
                ref={tableRef}
                bookablesByDate={bookablesByDateForWeek}
                pros={pros}
                emptyState={
                    <CalendarEmptyState
                        src="/icons/checklist.svg"
                        title="No appointments"
                        subtitle={`Try to select ${!filter.isEmpty ? 'different filters or' : ''} a different date range`}
                    />
                }
            />
        </Card>
    )
}
